import { supabase } from '@/lib/supabase';
import { Customer, CustomerInsert, CustomerUpdate, PaginatedResult, Sale, ReturnRecord, RepairJob } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface CustomerLedgerEntry {
  id: string;
  date: string;
  type: 'sale' | 'payment' | 'return_credit' | 'cash_refund' | 'repair';
  reference: string;
  referenceId: string;
  description: string;
  debit: number;   // Money customer owes shop (sales, repair charges, cash refunds)
  credit: number;  // Money shop received or credited customer (payments in, return credits)
  runningBalance: number;
}

export interface CustomerLedgerSummary {
  totalSales: number;
  totalPaymentsReceived: number;
  totalReturnCredit: number;
  totalCashRefunded: number;
  outstandingBalance: number;
  lifetimeNetRevenue: number;
  salesCount: number;
  repairsCount: number;
  entries: CustomerLedgerEntry[];
}

/**
 * Fetch a paginated, searchable list of customers from public.customers with archive filter.
 */
export async function fetchCustomers(params: {
  search?: string;
  page?: number;
  archivedFilter?: 'active' | 'archived' | 'all';
}): Promise<PaginatedResult<Customer>> {
  const { search = '', page = 0, archivedFilter = 'active' } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('customers')
    .select('*', { count: 'exact' })
    .order('name', { ascending: true })
    .range(from, to);

  if (archivedFilter === 'active') {
    query = query.is('archived_at', null);
  } else if (archivedFilter === 'archived') {
    query = query.not('archived_at', 'is', null);
  }

  if (search.trim()) {
    query = query.or(
      `name.ilike.%${search.trim()}%,phone.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%`
    );
  }

  const { data, error, count } = await query;

  if (error) throw error;

  return { data: (data ?? []) as Customer[], count: count ?? 0 };
}

/**
 * Fetch a single customer by id from public.customers.
 */
export async function fetchCustomerById(id: string): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Customer;
}

/**
 * Insert a new customer row into public.customers.
 */
export async function createCustomer(payload: CustomerInsert): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data as Customer;
}

/**
 * Update an existing customer in public.customers.
 */
export async function updateCustomer(id: string, payload: CustomerUpdate): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Customer;
}

/**
 * Delete a customer permanently (Owner only, blocked if relationships exist).
 */
export async function deleteCustomerRpc(customerId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_customer', {
    p_customer_id: customerId,
  });
  if (error) throw error;
}

/**
 * Archive a customer (Owner / Manager).
 */
export async function archiveCustomerRpc(customerId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('archive_customer', {
    p_customer_id: customerId,
    p_reason: reason,
  });
  if (error) throw error;
}

/**
 * Restore an archived customer (Owner / Manager).
 */
export async function restoreCustomerRpc(customerId: string): Promise<void> {
  const { error } = await supabase.rpc('restore_customer', {
    p_customer_id: customerId,
  });
  if (error) throw error;
}

/**
 * Fetch authoritative Customer Account Ledger with chronological timeline & summary metrics.
 */
export async function fetchCustomerLedger(customerId: string): Promise<CustomerLedgerSummary> {
  // 1. Fetch sales
  const { data: salesData, error: salesErr } = await supabase
    .from('sales')
    .select('*')
    .eq('customer_id', customerId)
    .neq('status', 'void')
    .order('sale_date', { ascending: true });

  if (salesErr) throw salesErr;
  const sales = (salesData ?? []) as Sale[];

  // 2. Fetch payments linked to those sales
  const saleIds = sales.map((s) => s.id);
  let payments: any[] = [];
  if (saleIds.length > 0) {
    const { data: payData, error: payErr } = await supabase
      .from('payments')
      .select('*')
      .in('sale_id', saleIds)
      .eq('direction', 'in')
      .order('payment_date', { ascending: true });

    if (!payErr && payData) {
      payments = payData;
    }
  }

  // 3. Fetch returns
  const { data: returnsData, error: retErr } = await supabase
    .from('returns')
    .select('*')
    .eq('customer_id', customerId)
    .eq('status', 'completed')
    .order('return_date', { ascending: true });

  if (retErr) throw retErr;
  const returns = (returnsData ?? []) as ReturnRecord[];

  // 4. Fetch repairs
  const { data: repairsData, error: repErr } = await supabase
    .from('repair_jobs')
    .select('*')
    .eq('customer_id', customerId)
    .neq('status', 'cancelled')
    .order('received_at', { ascending: true });

  if (repErr) throw repErr;
  const repairs = (repairsData ?? []) as RepairJob[];

  // Build Chronological Events
  const rawEvents: Array<{
    date: string;
    type: 'sale' | 'payment' | 'return_credit' | 'cash_refund' | 'repair';
    reference: string;
    referenceId: string;
    description: string;
    debit: number;
    credit: number;
  }> = [];

  for (const s of sales) {
    rawEvents.push({
      date: s.sale_date,
      type: 'sale',
      reference: s.invoice_number,
      referenceId: s.id,
      description: `Sales Invoice #${s.invoice_number}`,
      debit: Number(s.total) || 0,
      credit: 0,
    });
  }

  for (const p of payments) {
    rawEvents.push({
      date: p.payment_date || p.created_at,
      type: 'payment',
      reference: p.reference || 'Payment',
      referenceId: p.sale_id || p.id,
      description: `Payment received (${p.payment_method || 'Cash'})${p.reference ? ' - ' + p.reference : ''}`,
      debit: 0,
      credit: Number(p.amount) || 0,
    });
  }

  for (const r of returns) {
    const retVal = Number(r.return_value ?? r.refund_amount) || 0;
    const cashRefund = Number(r.refund_amount) || 0;

    if (retVal > 0) {
      rawEvents.push({
        date: r.return_date,
        type: 'return_credit',
        reference: r.return_number,
        referenceId: r.id,
        description: `Customer Return #${r.return_number}${r.reason ? ' (' + r.reason + ')' : ''}`,
        debit: 0,
        credit: retVal,
      });
    }

    if (cashRefund > 0) {
      rawEvents.push({
        date: r.return_date,
        type: 'cash_refund',
        reference: r.return_number,
        referenceId: r.id,
        description: `Cash Refund paid to customer #${r.return_number}`,
        debit: cashRefund,
        credit: 0,
      });
    }
  }

  for (const rep of repairs) {
    if (rep.total_charge > 0) {
      rawEvents.push({
        date: rep.received_at,
        type: 'repair',
        reference: rep.job_number,
        referenceId: rep.id,
        description: `Repair Ticket #${rep.job_number} - ${rep.reported_issue}`,
        debit: Number(rep.total_charge) || 0,
        credit: 0,
      });
    }
  }

  // Sort chronological
  rawEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Calculate running balance
  let running = 0;
  const entries: CustomerLedgerEntry[] = rawEvents.map((ev, idx) => {
    running += (ev.debit - ev.credit);
    return {
      id: `${ev.type}-${ev.referenceId}-${idx}`,
      ...ev,
      runningBalance: Math.max(0, running),
    };
  });

  const totalSales = sales.reduce((acc, s) => acc + (Number(s.total) || 0), 0);
  const totalPaymentsReceived = payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  const totalReturnCredit = returns.reduce((acc, r) => acc + (Number(r.return_value ?? r.refund_amount) || 0), 0);
  const totalCashRefunded = returns.reduce((acc, r) => acc + (Number(r.refund_amount) || 0), 0);
  const outstandingBalance = sales.reduce((acc, s) => acc + (Number(s.balance_due) || 0), 0);
  const lifetimeNetRevenue = Math.max(0, totalSales - (totalReturnCredit - returns.reduce((acc, r) => acc + (Number(r.deduction_retained) || 0), 0)));

  return {
    totalSales,
    totalPaymentsReceived,
    totalReturnCredit,
    totalCashRefunded,
    outstandingBalance,
    lifetimeNetRevenue,
    salesCount: sales.length,
    repairsCount: repairs.length,
    entries,
  };
}

